import type { NotificationApiRoot } from '@/features/activity/api/notification-api'
import type { GetCurrentMemberFn } from '@/features/iam'
import { collection } from '@/infra/collection'
import {
  type NotificationMediaPreferences,
  NotificationPreferences,
  type NotificationPreferencesProps,
} from '@/features/activity/NotificationPreferences'
import type {
  NotificationMediaPreferencesResource,
  NotificationPreferencesResource,
  UpdateNotificationPreferencesRequest,
} from '@/features/activity/api/resources/notification'
import { Id } from '@/infra/id'

/**
 * Manages notification preferences.
 */
export class NotificationPreferencesStore {
  readonly preferences = collection({
    create: () => new NotificationPreferences(),
    getModelId: (m) => `${m.userId}/${m.workspaceId}`,
    getDataId: (d) => `${d.userId}/${d.workspaceId}`,
  })

  constructor(
    private readonly _api: NotificationApiRoot,
    private readonly _getCurrentMember: GetCurrentMemberFn,
  ) {}

  async fetchPreferences(
    signal?: AbortSignal,
  ): Promise<NotificationPreferences> {
    const member = this._getCurrentMember()
    const userId = Id.assert(member.userId)
    const workspaceId = Id.assert(member.workspaceId)

    const response = await this._api.preferences.getPreferences(
      workspaceId,
      userId,
      signal,
    )

    const props = mapPreferencesResourceToProps(userId, workspaceId, response)
    return this.preferences.set(props)
  }

  async updatePreferences(
    command: UpdateNotificationPreferences,
  ): Promise<NotificationPreferences> {
    const member = this._getCurrentMember()
    const userId = Id.assert(member.userId)
    const workspaceId = Id.assert(member.workspaceId)

    const request: UpdateNotificationPreferencesRequest =
      mapUpdateCommandToRequest(command)

    const response = await this._api.preferences.updatePreferences(
      workspaceId,
      userId,
      request,
    )

    const props = mapPreferencesResourceToProps(userId, workspaceId, response)
    return this.preferences.set(props)
  }
}

function mapPreferencesResourceToProps(
  userId: string,
  workspaceId: string,
  resource: NotificationPreferencesResource,
): NotificationPreferencesProps {
  const types = new Map(
    Object.entries(resource.types_media).map(([template, mediaResource]) => [
      template,
      mapMediaResourceToProps(mediaResource),
    ]),
  )
  return {
    userId,
    workspaceId,
    types,
    media: mapMediaResourceToProps(resource.media),
  }
}

function mapMediaResourceToProps(
  media: NotificationMediaPreferencesResource,
): NotificationMediaPreferences {
  return {
    email: {
      enabled: media?.email?.enabled,
    },
    push: {
      enabled: media?.push?.enabled,
    },
  }
}

/**
 * Command for updating notification preferences.
 */
export interface UpdateNotificationPreferences {
  media: NotificationMediaPreferences
  types: Map<string, NotificationMediaPreferences>
}

function mapUpdateCommandToRequest(
  command: UpdateNotificationPreferences,
): UpdateNotificationPreferencesRequest {
  const typesMedia: UpdateNotificationPreferencesRequest['types_media'] = {}
  command.types.forEach((value, key) => {
    typesMedia[key] = {
      email: {
        enabled: value.email.enabled,
      },
      push: {
        enabled: value.push.enabled,
      },
    }
  })

  return {
    types_media: typesMedia,
    media: {
      email: {
        enabled: command.media.email.enabled,
      },
      push: {
        enabled: command.media.push.enabled,
      },
    },
  }
}
